/* eslint-disable react-refresh/only-export-components */
import { matchPage, preloadPage } from "@/pageRoutes";
import { appHost } from "@/utils/hosts";
import {
    AnchorHTMLAttributes,
    createContext,
    MouseEvent,
    useContext,
    useEffect,
    useSyncExternalStore,
} from "react";
import { navigate } from "wouter/use-browser-location";

/*
 * Turns any link the app produces — a route() URL on the app host, an SPA URL
 * or a bare path — into the path to navigate to inside the SPA. Returns null
 * when the target is not an SPA page and needs a full page load instead
 * (server-only routes such as magic-login or Stripe, and other sites).
 */
export function spaPath(url: string): string | null {
    let target: URL;
    try {
        target = new URL(url, window.location.origin);
    } catch {
        return null;
    }

    const isOurHost =
        target.host === window.location.host || target.host === appHost();
    if (!isOurHost || !matchPage(target.pathname)) {
        return null;
    }

    return target.pathname + target.search + target.hash;
}

type LeaveConfirmation = () => Promise<boolean>;

let leaveConfirmation: LeaveConfirmation | null = null;

/*
 * While mounted, navigateTo and Link ask confirm() before leaving the page,
 * also when a Link leads to a page outside the SPA. Browser back/forward and
 * full page loads started any other way are not covered.
 */
export function useLeaveConfirmation(confirm: LeaveConfirmation) {
    useEffect(() => {
        leaveConfirmation = confirm;

        return () => {
            if (leaveConfirmation === confirm) {
                leaveConfirmation = null;
            }
        };
    }, [confirm]);
}

/*
 * Every navigateTo is a new visit: AppRouter remounts the page and reports a
 * page view even when only the query string changed.
 * Browser back/forward and history entries other code makes (quiz steps, URL
 * param clean-ups) remount the page only when the pathname changes.
 */
let visitCount = 0;
const visitListeners = new Set<() => void>();

export function subscribeToVisits(listener: () => void) {
    visitListeners.add(listener);

    return () => {
        visitListeners.delete(listener);
    };
}

export function useVisitCount(): number {
    return useSyncExternalStore(subscribeToVisits, () => visitCount);
}

type NavigateOptions = {
    replace?: boolean;
    preserveScroll?: boolean;
};

export async function navigateTo(url: string, options: NavigateOptions = {}) {
    if (leaveConfirmation && !(await leaveConfirmation())) {
        return;
    }

    const path = spaPath(url);
    if (!path) {
        if (options.replace) {
            window.location.replace(url);
        } else {
            window.location.assign(url);
        }
        return;
    }

    navigate(path, { replace: options.replace ?? false });
    visitCount += 1;
    visitListeners.forEach((listener) => listener());
    if (!options.preserveScroll) {
        window.scrollTo(0, 0);
    }
}

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>) {
    return (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    );
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
    replace?: boolean;
    preserveScroll?: boolean;
};

export function Link({
    href,
    children,
    replace,
    preserveScroll,
    onClick,
    onMouseEnter,
    onFocus,
    onTouchStart,
    target,
    ...props
}: LinkProps) {
    const path = target ? null : spaPath(href);

    return (
        <a
            {...props}
            href={path ?? href}
            target={target}
            onClick={(event) => {
                onClick?.(event);
                const needsNavigateTo = !!path || !!leaveConfirmation;
                if (
                    !needsNavigateTo ||
                    target ||
                    event.defaultPrevented ||
                    isModifiedClick(event)
                ) {
                    return;
                }
                event.preventDefault();
                void navigateTo(path ?? href, { replace, preserveScroll });
            }}
            onMouseEnter={(event) => {
                onMouseEnter?.(event);
                if (path) {
                    preloadPage(new URL(path, window.location.origin).pathname);
                }
            }}
            onFocus={(event) => {
                onFocus?.(event);
                if (path) {
                    preloadPage(new URL(path, window.location.origin).pathname);
                }
            }}
            onTouchStart={(event) => {
                onTouchStart?.(event);
                if (path) {
                    preloadPage(new URL(path, window.location.origin).pathname);
                }
            }}
        >
            {children}
        </a>
    );
}

export const PageParamsContext = createContext<Record<string, string>>({});

export function usePageParams(): Record<string, string> {
    return useContext(PageParamsContext);
}

// The query string of the URL AppRouter renders the page for, which trails window.location while the next page loads.
export const PageSearchContext = createContext("");

export function usePageSearchParams(): URLSearchParams {
    return new URLSearchParams(useContext(PageSearchContext));
}
